import { Node, Editor, Transforms, Range, Point, Element } from 'slate';

import { BuiltInComponents, RichTextEditorNodes, TypographyVariants } from 'craft/constants';
import typographyStyles from 'craft/resolvers/Typography/constants';
import { getRandomId } from 'craft/utils';

const { List, ListItem, Typography } = BuiltInComponents;

const SHORTCUTS = {
  '1.': ListItem,
  '1)': ListItem,
  '*': ListItem,
  '-': ListItem,
  '#': Typography,
  '##': Typography,
  '###': Typography,
  '####': Typography,
  '#####': Typography,
  '######': Typography,
};

function isValidShortcut(value: string): value is keyof typeof SHORTCUTS {
  return value in SHORTCUTS;
}

const getType = (beforeText: string) => {
  if (!isValidShortcut(beforeText)) {
    return {};
  }
  const type = SHORTCUTS[beforeText];
  if (type === ListItem) {
    return {
      type,
      variant: beforeText.length > 1 ? 'OL' : 'UL',
      css: { paddingLeft: '40px', marginBottom: '16px' },
    };
  }
  // TODO is there no better way to un-confuse compiler than this ?!?
  if (String(type) === String(RichTextEditorNodes.Typography)) {
    return {
      type,
      variant: `H${beforeText.length}`,
      css: typographyStyles[`H${beforeText.length}` as TypographyVariants],
    };
  }
  return { type, css: {} };
};

export const withMarkdownShortcuts = <T extends Editor>(editor: T) => {
  const { deleteBackward, insertText } = editor;

  // eslint-disable-next-line
  editor.insertText = (text) => {
    const { selection } = editor;

    if (text !== ' ' || !selection || Range.isExpanded(selection)) {
      return insertText(text);
    }

    const { anchor } = selection;
    const block = Editor.above(editor, {
      match: (n) => Editor.isBlock(editor, n),
    });
    const path = block ? block[1] : [];
    const start = Editor.start(editor, path);
    const range = { anchor, focus: start };
    const beforeText = Editor.string(editor, range);
    const { type, variant, css } = getType(beforeText);

    if (!type) {
      return insertText(text);
    }

    Editor.withoutNormalizing(editor, () => {
      Transforms.select(editor, range);
      Transforms.delete(editor);
      Transforms.setNodes(
        editor,
        { type, data: { css: {} } },
        { match: (n) => Editor.isBlock(editor, n) },
      );

      if (type === ListItem) {
        const list = {
          id: getRandomId(),
          type: BuiltInComponents.List,
          data: { variant, css },
          children: [],
        };
        Transforms.wrapNodes(editor, list, {
          match: (n) => Element.isElement(n) && n.type === BuiltInComponents.ListItem,
        });
      } else {
        Transforms.setNodes(
          editor,
          { type, data: { variant, css } },
          { match: (n) => Editor.isBlock(editor, n) },
        );
      }
    });
  };

  // eslint-disable-next-line no-param-reassign
  editor.deleteBackward = (...args) => {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const match = Editor.above(editor, {
        match: (n) => Editor.isBlock(editor, n),
      });

      if (match) {
        const [block, path] = match;
        const start = Editor.start(editor, path);

        if (
          Element.isElement(block) &&
          block.type !== Typography &&
          Point.equals(selection.anchor, start)
        ) {
          const parent = Node.parent(editor, path);
          let inSublist = false;

          if (path.length > 2) {
            const grandparentPath = path.slice(0, path.length - 1);
            const grandparent = Node.parent(editor, grandparentPath);

            if (
              Element.isElement(parent) &&
              parent.type === BuiltInComponents.List &&
              Element.isElement(grandparent) &&
              grandparent.type === BuiltInComponents.List
            ) {
              inSublist = true;
            }
          }

          if (!inSublist) {
            Transforms.setNodes(editor, {
              type: Typography,
              data: { variant: TypographyVariants.P, css: typographyStyles.P },
            });
          }

          if (block.type === ListItem) {
            Transforms.unwrapNodes(editor, {
              match: (n) => Element.isElement(n) && n.type === List,
              split: true,
            });
          }

          return;
        }
      }

      deleteBackward(...args);
    }
  };

  return editor;
};
